import { handleJsonResponse, handleFileUploadResponse, getFilterQuery, addSortingQuery, getXsrfToken } from "./api";
import config from '../config'; 
import { getUser } from '../auth';
import { upload } from "@testing-library/user-event/dist/upload";

async function getFileById (id, token) {
  const response = await fetch(`${config.API_URL}/files/${id}`,
  {
  headers: {
    Authorization: `Bearer ${token}`
  }
  });

  return await handleJsonResponse(response);
}

export async function getFilesById(fileIds) {
  const user = getUser();
  const token = user?.access_token;
  try {
    const fetchFilesPromises = fileIds.map(id => getFileById(id, token));
    return await Promise.all(fetchFilesPromises);
  } catch (error) {
  }

}

export async function getFiles(filters, sorting, start = 0, limit = 10) {
  const user = getUser();
  const token = user?.access_token;

  let filterQuery = getFilterQuery(filters);
  
  if (filterQuery.length > 0){
    filterQuery = "filter=" + filterQuery;
  }
  filterQuery = addSortingQuery(filterQuery, sorting);
  const response = await fetch(`${config.API_URL}/files?${filterQuery}start=${start}&limit=${limit}`,    
  {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  return await handleJsonResponse(response);
}

export async function deleteFiles(files) {
  const user = getUser();
  const token = user?.access_token;
  const fileIds = files.map((file) => file.id);
  const data = JSON.stringify(fileIds);

  const response = await fetch(`${config.API_URL}/files/batch-archive`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getXsrfToken()
    },
    body: data
  });
  return await handleJsonResponse(response);

} 

export async function downloadFile(fileData, updateProgress, download_id) {
  
  const user = getUser();
  const token = user?.access_token;
  
  
   try {
   const response = await fetch(`${config.API_URL}/files/${fileData.id}/data`, {
     headers: {
       Authorization: `Bearer ${token}`
     }
   });

   if (!response.ok) {
     throw new Error(`Status Code: ${response.status} ${response.statusText}`);
   }

   // Get file info from headers
   const contentLength = response.headers.get('Content-Length');
   const contentDisposition = response.headers.get('Content-Disposition');
   const filename = contentDisposition 
     ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
     : fileData.fileName;

   // Stream the response for large files
   const reader = response.body.getReader();
   const chunks = [];
   let receivedLength = 0;

   while (true) {
     const { done, value } = await reader.read();
     
     if (done) break;
     
     chunks.push(value);
     receivedLength += value.length;
     
     // Report progress if callback provided
     if (updateProgress && contentLength) {
       const progress = (receivedLength / parseInt(contentLength)) * 100;
       updateProgress(download_id, progress);
     }
     
   }

   // Concatenate chunks into single array
   const chunksAll = new Uint8Array(receivedLength);
   let position = 0;
   for (const chunk of chunks) {
     chunksAll.set(chunk, position);
     position += chunk.length;
   }

   // Create blob and download
   const blob = new Blob([chunksAll], { type: response.headers.get('Content-Type') });
   const blobUrl = URL.createObjectURL(blob);
   const link = document.createElement('a');
   link.href = blobUrl;
   link.download = filename || 'download';
   document.body.appendChild(link);
   link.click();
   
   // Cleanup after a delay
   setTimeout(() => {
     link.remove();
     URL.revokeObjectURL(blobUrl);
   }, 100);

   return { status: "success", message: "File downloaded successfully", id: fileData.id };
 } catch (error) {
   return { status: "error", message: `Error downloading file: ${error.message}`, id: fileData.id, download_id :download_id };
 }
}


export async function downloadFiles(filesInfo, updateProgress) {
  const fileResponses = filesInfo.map((fileInfo) => 
  {
    return downloadFile(fileInfo.file, updateProgress, fileInfo.download_id);
  });
  return fileResponses;
}

export async function deleteFile(fileId) {
  const user = getUser();
  const token = user?.access_token;
  return await changeState(fileId, "archive", token);
}

export async function uploadFiles(toBeUploadedFiles, updateStatus, updateMessage) {
  
  const user = getUser();
  const token = user?.access_token;
  
  const results = await Promise.all(toBeUploadedFiles.map(async (file, index) => {
    let fileId = "";
    try
    {
      
      const uploadData = {
        Mimetype: file.type === '' ? "application/octet-stream" : file.type,
        FileSize: file.size,
        FileName: file.name,
        SupportedMethods: ["OutOfBandHttpPost"]
      }
      updateStatus(index, "Pending");

      const response = await fetch(`${config.API_URL}/files/inputs/`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-XSRF-TOKEN': getXsrfToken()
        },
        body: JSON.stringify(uploadData)
      });
      
      const newFileResponse = await handleJsonResponse(response);
      fileId = newFileResponse.id;

      const uploadingStatusResponse = await changeState(fileId, "Uploading", token); 

      const fileBlob = new Blob([file.file], {type: file.type});
      const putResponse = await fetch(`${newFileResponse.connectionString}`,{ 
        method: 'PUT',
        body: fileBlob,
        
        headers: {  
          'Content-Type': `${file.type}`,
          'Content-Length': `${file.size}`,
          'x-ms-blob-type': "BlockBlob", 
        }, 
      });

      const uploadFileResponse = await handleFileUploadResponse(putResponse);
      updateStatus(index, "uploadComplete");


      const statusChange = await changeState(fileId, "UploadComplete", token);
      index++;
      
      return {status: "success", message: `File uploaded successfully ${newFileResponse.id}`, id: newFileResponse.id};

    } catch(error){
      let message = "Error uploading file. No id returned";
      if(fileId)
      {
        const fileStatusData ={"type": "Failed"};
        const response = await postFileAction(fileId, fileStatusData, token).then((response) => {
          if(!response.ok)
          {
            return "Error uploading file and changing file state"
          }
          return "Error uploading file"});          
          message = response;
      }
      updateStatus(index, "error");
      updateMessage(index, message);
      index++;
      return {status: "error", message: message};
    }
      
  }));
  return results;  
}

export async function changeState(fileId, state, token) {
  const data = JSON.stringify({"type": state});
  const response = await postFileAction(fileId, data, token);
  
  return await handleJsonResponse(response);
}

export async function postFileAction(fileId, data, token) {
  const response = await fetch(`${config.API_URL}/files/${fileId}/actions`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      'X-XSRF-TOKEN': getXsrfToken()
    },
    body: data
  });
  
  return response;
}